import { NewbuildingCarousel } from '@cian/frontend-newbuilding-carousel-component';

import { type FC, useCallback, useState } from 'react';

import { Layout } from 'shared/components/Layout';

import { firstPaintOptions, mediaSettings } from '../Carousel/config';
import { DATA_AS_NAV_TAB_ATTR } from '../Header/internal/utils/dataAsNavTabAttr';

import { SERIA_PLUS_SLIDE_DURATION_MS } from './internal/constants';
import { SeriaPlusBottomContent } from './internal/components/SeriaPlusBottomContent';
import { SeriaPlusCarouselControls } from './internal/components/SeriaPlusCarouselControls';
import { SeriaPlusHeader } from './internal/components/SeriaPlusHeader';
import { SeriaPlusMarkers } from './internal/components/SeriaPlusMarkers';
import { SeriaPlusSection } from './internal/components/SeriaPlusSection';
import { SeriaPlusSlide } from './internal/components/SeriaPlusSlide';
import { useSeriaPlusInteractionPause } from './internal/hooks/useSeriaPlusInteractionPause';
import {
  type SeriaPlusScheme,
  type SeriaPlusSlideMarker,
  type SeriaPlusTabScheme,
} from './internal/settings/seriaPlusTypes';
import styles from './SeriaPlusV2.css';

type SeriaPlusProps = {
  scheme: SeriaPlusScheme;
};

const EMPTY_SERIA_PLUS_SLIDES: SeriaPlusTabScheme['slides'] = [];
const EMPTY_SERIA_PLUS_MARKERS: SeriaPlusSlideMarker[] = [];

export const SeriaPlusV2: FC<SeriaPlusProps> = props => {
  const {
    scheme: { tabs, description, title, sectionTabTitle, watchObjectsLink },
  } = props;

  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(1);
  const {
    handleProgressBarMouseEnter,
    handleProgressBarMouseLeave,
    handleTooltipClose,
    handleTooltipOpen,
    isInteractionPaused,
  } = useSeriaPlusInteractionPause();

  const activeTab = tabs[activeTabIndex] || tabs[0];
  const activeSlides = activeTab?.slides || EMPTY_SERIA_PLUS_SLIDES;
  const activeSlideIndex = currentSlide - 1;
  const activeSlide = activeSlides[activeSlideIndex] || activeSlides[0];
  const activeMarkers = activeSlide?.markers || EMPTY_SERIA_PLUS_MARKERS;
  const hasMultipleSlides = activeSlides.length > 1;

  const handleAnimationEnd = useCallback((): void => {
    setCurrentSlide(current => (current < activeSlides.length ? current + 1 : 1));
  }, [activeSlides.length]);

  const handleTabChange = useCallback(
    (selectedTabIndexes: number[]): void => {
      const nextTabIndex = selectedTabIndexes[0];

      if (typeof nextTabIndex !== 'number' || nextTabIndex === activeTabIndex) {
        return;
      }

      setActiveTabIndex(nextTabIndex);
      setCurrentSlide(1);
    },
    [activeTabIndex],
  );

  const handleProgressClick = useCallback((slideIndex: number): void => {
    setCurrentSlide(slideIndex + 1);
  }, []);

  const handlePreviousSlideClick = useCallback((): void => {
    if (!hasMultipleSlides) {
      return;
    }

    const previousSlideIndex = (activeSlideIndex - 1 + activeSlides.length) % activeSlides.length;

    setCurrentSlide(previousSlideIndex + 1);
  }, [activeSlideIndex, hasMultipleSlides, activeSlides.length]);

  const handleNextSlideClick = useCallback((): void => {
    if (!hasMultipleSlides) {
      return;
    }

    const nextSlideIndex = (activeSlideIndex + 1) % activeSlides.length;

    setCurrentSlide(nextSlideIndex + 1);
  }, [activeSlideIndex, activeSlides, hasMultipleSlides]);

  if (!activeTab || !activeSlide) {
    return null;
  }

  const tabAttributes = sectionTabTitle ? { [DATA_AS_NAV_TAB_ATTR]: sectionTabTitle } : null;

  return (
    <SeriaPlusSection {...tabAttributes}>
      <Layout>
        <div className={styles['layout']}>
          <div
            aria-label={`${title}. ${description}`}
            className={styles['carousel']}
            data-testid="seria-plus-carousel-container"
          >
            <div className={styles['slides']} data-testid="seria-plus-carousel-content">
              <NewbuildingCarousel
                defaultCurrentSlide={currentSlide}
                firstPaintOptions={firstPaintOptions}
                key={activeTab.id}
                media={mediaSettings}
                slides={activeSlides.map((slide, index) => {
                  const isActive = slide.id === activeSlide.id;
                  const isEager = index === 0 && isActive;
                  const key = `${activeTab?.id}-${slide.id}`;

                  return <SeriaPlusSlide key={key} isActive={isActive} isEager={isEager} slide={slide} />;
                })}
                onSlideChange={onSlideChangeParams => setCurrentSlide(onSlideChangeParams.currentSlide)}
              />
            </div>

            <div className={styles['foreground']}>
              <SeriaPlusHeader
                activeTabIndex={activeTabIndex}
                title={title}
                tabs={tabs}
                onTabChange={handleTabChange}
              />
              <SeriaPlusBottomContent
                activeSlideIndex={activeSlideIndex}
                activeSlides={activeSlides}
                activeTabIndex={activeTabIndex}
                isAutoplayPaused={isInteractionPaused}
                slideDuration={SERIA_PLUS_SLIDE_DURATION_MS}
                tabs={tabs}
                watchObjectsLink={watchObjectsLink}
                onAnimationEnd={handleAnimationEnd}
                onProgressBarHover={handleProgressBarMouseEnter}
                onProgressBarLeave={handleProgressBarMouseLeave}
                onProgressClick={handleProgressClick}
              />
            </div>

            <SeriaPlusMarkers
              markers={activeMarkers}
              onTooltipClose={handleTooltipClose}
              onTooltipOpen={handleTooltipOpen}
            />
            <SeriaPlusCarouselControls
              hasMultipleSlides={hasMultipleSlides}
              onNextSlideClick={handleNextSlideClick}
              onPreviousSlideClick={handlePreviousSlideClick}
            />
          </div>
        </div>
      </Layout>
    </SeriaPlusSection>
  );
};
